<template>
  <div class="favourite-panel favourite-panel--empty">
    <template v-if="favourites && favourites.length">
      <div class="favourite-panel__filters">
        <app-input
          v-model="searchText"
          class="favourite-panel__filters-input"
          :placeholder="$t('search_favourite')"
        />
        <app-tooltip
          position="top"
          :text="$t(showProjects ? 'chat_filter_projects_off' : 'chat_filter_projects_on')"
        >
          <template #activator>
            <div
              :class="{
                'favourite-panel__filters-toggle': true,
                'favourite-panel__filters-toggle--selected': showProjects,
              }"
              @click.stop="showProjects = !showProjects"
            >
              {{ $t('projects_label') }}
            </div>
          </template>
        </app-tooltip>
        <app-tooltip
          position="top"
          :text="$t(showClients ? 'chat_filter_clients_off' : 'chat_filter_clients_on')"
        >
          <template #activator>
            <div
              :class="{
                'favourite-panel__filters-toggle': true,
                'favourite-panel__filters-toggle--selected': showClients,
              }"
              @click.stop="showClients = !showClients"
            >
              {{ $t('clients_label') }}
            </div>
          </template>
        </app-tooltip>
        <app-tooltip v-if="!sortType" position="top" :text="$t('sort_tip_str_asc')">
          <template #activator>
            <app-icon
              v-if="!sortType"
              class="favourite-panel__filters-sort"
              name="sort-auto"
              width="18px"
              color="#979797"
              hover-color="#499259"
              cursor="pointer"
              @click.stop="changeFavouriteSort"
            />
          </template>
        </app-tooltip>
        <app-tooltip
          v-else-if="sortType === 'asc'"
          position="top"
          :text="$t('sort_tip_str_desc')"
        >
          <template #activator>
            <app-icon
              class="favourite-panel__filters-sort"
              name="sort-a-z"
              width="18px"
              color="#979797"
              hover-color="#499259"
              cursor="pointer"
              @click.stop="changeFavouriteSort"
            />
          </template>
        </app-tooltip>
        <app-tooltip v-else position="top" :text="$t('sort_tip_default')">
          <template #activator>
            <app-icon
              class="favourite-panel__filters-sort"
              name="sort-z-a"
              width="18px"
              color="#979797"
              hover-color="#499259"
              cursor="pointer"
              @click.stop="changeFavouriteSort"
            />
          </template>
        </app-tooltip>
      </div>
      <div class="favourite-panel__divider" />
      <div class="favourite-panel__body">
        <div
          class="favourite-panel__body-item"
          v-for="favourite in filteredFavourites"
          :key="favourite.Client?.Id || favourite.Project?.Id"
        >
          <app-tooltip
            class="favourite-panel__body-item-icon"
            :text="$t('favourite_remove')"
          >
            <template #activator>
              <app-icon
                name="favourite"
                width="18px"
                color="#D05A4F"
                hover-color="#979797"
                cursor="pointer"
                @click="deleteFromFavourites(favourite)"
              />
            </template>
          </app-tooltip>
          <div class="favourite-panel__body-item-text">
            <div class="favourite-panel__body-item-text-type">
              {{ $t(favourite.Type === 0 ? 'client_label' : 'project_label') }}
            </div>
            <router-link
              :to="{
                query: {
                  page: favourite.Type === 0 ? 'client' : 'project',
                  id:
                    favourite.Type === 0
                      ? favourite.Client?.Id
                      : favourite.Project?.Id,
                },
              }"
              class="favourite-panel__body-item-text-name"
            >
              <span v-html="getFavouriteName(favourite)" />
            </router-link>
            <div class="favourite-panel__body-item-text-description">
              {{ getFavouriteDescription(favourite) }}
            </div>
          </div>
        </div>
        <div
          v-if="searchText && !filteredFavourites?.length"
          class="favourite-panel__body-empty"
        >
          {{ $t('nothing_found') }}
        </div>
      </div>
    </template>
    <div v-else class="favourite-panel__empty">
      {{ $t('chat_favourite_empty') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { Favourite, favouritesStore } from "@/store/favourites";
import { escapeHtml, highlightSearchText } from "@/utils/textHelpers";
import { orderBy } from "lodash";
import { useRoute } from "vue-router";

const route = useRoute();
const store = favouritesStore();
const favourites = computed(() => store.favouritesList);
const sortType = ref<"asc" | "desc" | null>(null);
const showClients = ref(true);
const showProjects = ref(true);
const searchText = ref("");

const filteredFavourites = computed(() => {
  if (!favourites.value) return null;

  let modifiedFavourites = favourites.value.filter((favourite) =>
    favourite.Title.toLowerCase().includes(searchText.value.toLowerCase()),
  );

  if (!showClients.value) {
    modifiedFavourites = modifiedFavourites.filter(
      (favourite) => favourite.Type !== 0,
    );
  }

  if (!showProjects.value) {
    modifiedFavourites = modifiedFavourites.filter(
      (favourite) => favourite.Type !== 1,
    );
  }

  return sortType.value
    ? orderBy(
        modifiedFavourites,
        ["Type", "Title"],
        [sortType.value, sortType.value],
      )
    : modifiedFavourites;
});

const changeFavouriteSort = () => {
  switch (sortType.value) {
    case "asc":
      sortType.value = "desc";
      return;
    case "desc":
      sortType.value = null;
      return;
    default:
      sortType.value = "asc";
      return;
  }
};

const getFavouriteName = (favourite: Favourite) => {
  const name =
    favourite.Type === 0
      ? (favourite.Client?.Name ?? "")
      : (favourite.Project?.Name ?? "");

  return highlightSearchText(searchText.value, escapeHtml(name));
};

const getFavouriteDescription = (favourite: Favourite) => {
  return favourite.Type === 0
    ? mapParams(favourite.Client!.Parameters)
    : mapParams(favourite.Project!.Parameters);
};

const mapParams = (param: object[]) => {
  return param
    .map((p) =>
      p.Instance
        ? `${p.Instance.Key}: ${p.Instance.Value}`
        : mapParams(p.ListTitle),
    )
    .flat(2)
    .join(", ");
};

const deleteFromFavourites = async (favourite: Favourite) => {
  if (favourite.Type === 0) {
    await store.removeFavourites(favourite.Client!.Id, favourite.Type);
  } else {
    await store.removeFavourites(favourite.Project!.Id, favourite.Type);
  }
};

onMounted(() => {
  store.fetchFavourites(searchText.value);
});
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.favourite-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #fff;
  border-radius: 8px;
  height: 100%;
  max-height: 100%;

  &--empty {
    justify-content: center;
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px;

    &-input {
      flex: 1;
    }

    &-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 18px;
      width: 59px;

      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 14px;
      border-radius: 6px;
      padding: 0 4px;
      background-color: $text-color-btn;
      box-shadow: 0 0 1px 0.5px #d2d2d2 inset;
      color: #979797;
      cursor: pointer;

      &:hover {
        background-color: #f3f8f4;
      }

      &--selected {
        background-color: #6da279;
        color: #ffffff;

        &:hover {
          background-color: rgba(109, 162, 121, 0.5);
        }
      }
    }
  }

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 4px;
    overflow: auto;

    &-empty {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: #979797;
      font-size: 12px;
      line-height: 16px;
    }

    &-item {
      display: flex;
      gap: 4px;

      &-icon {
        margin-top: -1px;
      }

      &-text {
        display: inline;

        &-type,
        &-name,
        &-description {
          display: inline;
          font-size: 14px;
          line-height: 18px;
          margin-right: 4px;
        }

        &-type,
        &-description {
          color: #4d4d4d;
        }

        &-name {
          color: #438451;
          cursor: pointer;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }

  &__divider {
    width: 100%;
    border-bottom: 1px solid #f2f2f2;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333333;
    font-size: 12px;
    line-height: 16px;
  }
}
</style>
